import { HttpClient } from "@angular/common/http";
import { Injectable } from "@angular/core";
import { Observable } from "rxjs/internal/Observable";
import { Subject } from "rxjs/internal/Subject";
import { OfficeSelectorService } from "src/app/common/officeSelector/officeSelector.service";
import { AccessManagerService } from "../accessManager/accessManager.service";
import { LoggerService } from "src/app/common/logging/logger.service";
import { Subscription } from "rxjs/internal/Subscription";
import { AccessManagerResourceActionType } from "../accessManager/accessManager.models";
import { ImpersonationCfgService } from "./impersonationCfg.service";
import { ApigeeService } from "./apigee.service";

export enum ProfileType {
  OfficeManager,
  Consultant
}

export class ImpersonationProfile {
  public nif: string;
  private mName: string;
  private mLastName: string;
  private mSecondLastName: string;
  public email: string;

  public get name(): string {
    return this.mName || '';
  }
  public get lastName(): string {
    return this.mLastName || '';
  }
  public get secondLastName(): string {
    return this.mSecondLastName || '';
  }
  public get displayName(): string {
    return `${this.name} ${this.lastName}`.trim();
  }
  public get fullName(): string {
    return `${this.name} ${this.lastName} ${this.secondLastName}`.trim();
  }

  public image: string;

  constructor(nif: string, name: string, lastName: string, secondLastName: string, email: string) {
    this.nif = nif;
    this.mName = name;
    this.mLastName = lastName;
    this.mSecondLastName = secondLastName;
    this.email = email;
  }
}

export class OfficeTeam {
  public members: OfficeMemberProfile[] = [];
}

export class OfficeMemberProfile {
  public officeId: string;
  public nif: string;
  public data: OfficeMemberProfileData;
}

export class OfficeMemberProfileData {
  public name: string;
  public lastName: string;
  public secondLastName: string;
  public email: string;
  public employeeType: string;
  public employee: string;
  public positionCode: string;
  public position: string;
  public companyId: string;
  public businessBranch: string;
  public businessLocation: string;
  public costCenter: string;
  public managerId: string;
  public manager: string;
}

@Injectable({
  providedIn: 'root'
})
export class ImpersonationService {
  private impersonationChangedSource = new Subject<ImpersonationProfile | null>();
  public readonly impersonationChanged: Observable<ImpersonationProfile | null> = this.impersonationChangedSource.asObservable();

  private impersonatedProfile: ImpersonationProfile = null;
  private officeSelectorSubscription: Subscription;
  private impersonationEnabled = false;
  private readonly GOOGLE_CREDENTIAL: string = 'googleAuth';

  constructor(private serviceCfg: ImpersonationCfgService, private apigeeService: ApigeeService,
    private accessManager: AccessManagerService, private officeSelectorService: OfficeSelectorService,
    private httpClient: HttpClient,
    private logger: LoggerService) {
    this.officeSelectorSubscription = this.officeSelectorService.selectedOfficeUpdated.subscribe((office) => {
      this.clearImpersonation();
    });
  }

  /**
   * Inicializa el servicio verificando si el usuario tiene permisos para suplantar identidades.
   */
  public async initialize(): Promise<void> {
    if (this.apigeeService.hasToken) {
      this.impersonationEnabled = await this.requestCanImpersonate();
    }
  }

  /**
   * Indica si actualmente hay una suplantación de identidad activa.
   */
  public get isImpersonating(): boolean {
    return this.impersonatedProfile != null;
  }

  /**
   * Retorna el NIF del perfil suplantado, o null si no hay suplantación activa.
   */
  public get fiscalDocNumber(): string | null {
    return this.impersonatedProfile?.nif ?? null;
  }

  /**
   * Retorna el tipo de perfil actual: OfficeManager si puede suplantar y no está suplantando, Consultant en caso contrario.
   */
  public get currentProfileType(): ProfileType {
    if (this.impersonationEnabled && !this.impersonatedProfile) {
      return ProfileType.OfficeManager;
    } else {
      return ProfileType.Consultant;
    }
  }

  /**
   * Indica si el usuario puede suplantar identidades según sus permisos y la oficina seleccionada (debe ser TEA)
   */
  public get canImpersonate(): boolean {
    let officeFilter = false;
    if (this.officeSelectorService.selectedOffice) {
      officeFilter = officeFilter || this.officeSelectorService.selectedOffice.isTEA;
    }
    return this.impersonationEnabled && officeFilter;
  }

  /**
   * Retorna la imagen de perfil del usuario suplantado o del usuario autenticado si no hay suplantación.
   * @returns Imagen de perfil del usuario correspondiente.
   */
  public getProfileImage(): string {
    if (this.isImpersonating) {
      return this.impersonatedProfile.image;
    } else {
      return this.apigeeService.getProfileImage();
    }
  }

  /**
   * Retorna el nombre para mostrar del usuario suplantado o del usuario autenticado si no hay suplantación.
   * @returns Nombre para mostrar del usuario correspondiente.
   */
  public getUserDisplayName(): string {
    if (this.isImpersonating) {
      return this.impersonatedProfile.displayName;
    } else {
      return this.apigeeService.getUserDisplayName();
    }
  }

  /**
   * Retorna el nombre formateado para gráficas (Apellido1 Apellido2, Nombre) del usuario suplantado, o null si no hay suplantación.
   * @returns  Retorna el nombre formateado para gráficas del usuario correspondiente.
   */
  public getChartUserDisplayName() {
    if (this.isImpersonating) {
      return "{0} {1}, {2}".format(this.impersonatedProfile.lastName, this.impersonatedProfile.secondLastName, this.impersonatedProfile.name);
    } else {
      return null;
    }
  }

  /**
   * Activa la suplantación de identidad con el perfil indicado. Lanza un error si el usuario no tiene permisos.
   * @param profile Datos del perfil a suplantar.
   */
  public async impersonate(profile: ImpersonationProfile): Promise<void> {
    if (this.impersonationEnabled) {
      profile.image = "/assets/auth/google/user.png";
      this.impersonatedProfile = profile;
      this.impersonationChangedSource.next(profile);
    } else {
      throw new Error("Unauthorized operation")
    }
  }

  /**
   * Elimina la suplantación de identidad activa y notifica el cambio a los suscriptores.
   */
  public clearImpersonation(): void {
    if (this.impersonatedProfile) {
      this.impersonatedProfile = null;
      this.impersonationChangedSource.next(null);
    }
  }

  /**
   * Cierra la sesión del usuario, limpiando la suplantación activa y cerrando la sesión en RaaS.
   */
  public logout(): void {
    this.clearImpersonation();
    this.apigeeService.logout();
  }

  /**
   * Retorna el NIF del usuario actual sin lanzar excepciones; retorna cadena vacía si ocurre un error.
   * @returns Documento del usuario correspondiente.
   */
  public getNifSafe(): string {
    try {
      return this.getNif()
    } catch {
      return "";
    }
  }

  /**
   * Retorna el NIF del usuario suplantado o del usuario autenticado si no hay suplantación activa.
   * @returns  Documento del usuario correspondiente
   */
  public getNif(): string {
    if (this.isImpersonating) {
      return this.impersonatedProfile.nif;
    } else {
      return this.apigeeService.userInfo.nameid;
    }
  }

  /**
   * Retorna un objeto con el FiscalDocNumber si hay suplantación activa, o un objeto vacío en caso contrario.
   * @returns Objeto para el api
   */
  public getNifImpersonate(): { FiscalDocNumber?: string } {
    return this.isImpersonating
      ? { FiscalDocNumber: this.impersonatedProfile.nif }
      : {};
  }

  /**
   * Retorna el email del usuario suplantado o del usuario autenticado si no hay suplantación activa
   * @returns Email del usuario correspondiente
   */
  public getEmail(): string {
    if (this.isImpersonating) {
      return this.impersonatedProfile.email;
    } else {
      return this.apigeeService.userInfo.sub;
    }
  }

  /**
   * Obtiene el equipo de la oficina indicada o de la oficina seleccionada actualmente. Retorna un equipo vacío si no hay oficina
   * @param [officeId] Identificador de la oficina. Si se omite, se usa la oficina seleccionada.
   * @returns Retorna el equipo de la oficina indicada.
   */
  public async getOfficeTeam(officeId: string = null): Promise<OfficeTeam> {
    let office = this.officeSelectorService.selectedOffice;
    if (officeId || office) {
      let resolvedOfficeId = officeId || office.id;
      let endpoint = this.serviceCfg.getCfg().officeTeamEndpoint.format(resolvedOfficeId);
      let officeTeam = await this.httpClient.get<OfficeTeam>(endpoint).toPromise();
      return officeTeam;
    } else {
      return new OfficeTeam();
    }
  }

  /**
   * Retorna la lista de perfiles disponibles para suplantar en la oficina indicada o en la oficina seleccionada
   * @param [officeId] Identificador de la oficina. Si se omite, se usa la oficina seleccionada.
   * @returns Retorna la lista de perfiles disponibles para suplantar en la oficina indicada.
   */
  public async getImpersonableProfiles(officeId: string = null): Promise<ImpersonationProfile[]> {
    let officeTeam = await this.getOfficeTeam(officeId);
    return officeTeam.members.map(x => new ImpersonationProfile(x.nif, x.data.name, x.data.lastName, x.data.secondLastName, x.data.email));
  }

  /**
   * Consulta al AccessManager si el usuario tiene permisos de suplantación, validando también la credencial de Google.
   * @returns Retorna verdadero si el usuario puede suplantar identidades.
   */
  private async requestCanImpersonate(): Promise<boolean> {
    await this.serviceCfg.initialized;
    let cfg = this.serviceCfg.getCfg();

    try {
      const googleAuth = localStorage.getItem(this.GOOGLE_CREDENTIAL);

      if (googleAuth && this.apigeeService.isTokenValid() == '') {
        let result = await Promise.all([
          this.accessManager.getResourcePermissions(cfg.impersonationEnabledResourceId)
        ]);

        return result[0].getResourceAction(AccessManagerResourceActionType.WindowAccess);
      }
      else {
        return false;
      }
    } catch (error) {
      console.error(error);
      return false;
    }
  }

  /**
   * Cancela la suscripción al selector de oficina al destruir el servicio para evitar fugas de memoria.
   */
  public ngOnDestroy() {
    if (this.officeSelectorSubscription) {
      this.officeSelectorSubscription.unsubscribe();
    }
  }
}
